import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { AgGridReact } from 'ag-grid-react';
import AppContext from 'AppContext';
import GrpButton from 'Library/GrpButton/GrpButton';
import GrpSpinner from 'Components/GrpSpinner/GrpSpinner';
import {
  getTargetProfileById,
  createTargetProfile,
  updateTargetProfile,
  getReportTypes,
  getReportsByProfileId,
  createTargetReport,
  updateTargetReport,
  deleteTargetReport,
  getAllReportLanguages,
  createReportLanguage,
  deleteReportLanguage,
  getNamingConventions,
  getClientLanguages,
  getScopes,
  getTargetsFtps,
  getTargetProfileDistEmails,
  registerFtp,
} from 'Services/distributionApi';
import classes from './TargetProfilePage.module.scss';

const TargetProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const context = useContext(AppContext);
  const reportsGridRef = useRef(null);

  const isEditMode = !!id;

  const [loading, setLoading] = useState(true);
  const [profileName, setProfileName] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [isRestricted, setIsRestricted] = useState(false);

  // Report types + selected reports
  const [reportTypes, setReportTypes] = useState([]);
  const [selectedReportIds, setSelectedReportIds] = useState(new Set());
  const [existingReports, setExistingReports] = useState([]);
  const [reportConfigs, setReportConfigs] = useState({});

  // Options
  const [scopes, setScopes] = useState([]);
  const [selectedScopeId, setSelectedScopeId] = useState('');
  const [archiveSingleZip, setArchiveSingleZip] = useState(false);
  const [archiveEncrypted, setArchiveEncrypted] = useState(false);
  const [archivePassword, setArchivePassword] = useState('');
  const [documentPreference, setDocumentPreference] = useState('PerFund');

  // Lookups
  const [namingConventions, setNamingConventions] = useState([]);
  const [clientLanguages, setClientLanguages] = useState([]);
  const [allReportLanguages, setAllReportLanguages] = useState([]);

  // Delivery method
  const [deliveryMethod, setDeliveryMethod] = useState('None');
  const [ftpTargets, setFtpTargets] = useState([]);
  const [emailTargets, setEmailTargets] = useState([]);
  const [selectedFtpId, setSelectedFtpId] = useState('');
  const [selectedEmailId, setSelectedEmailId] = useState('');

  // Modals
  const [activeModal, setActiveModal] = useState(null);
  const [modalReportId, setModalReportId] = useState(null);

  // Load all data
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const [
          rtResult, scopesResult, ncResult, clResult, arlResult, ftpResult, emailResult,
        ] = await Promise.all([
          getReportTypes(),
          getScopes(),
          getNamingConventions(),
          getClientLanguages(),
          getAllReportLanguages(),
          getTargetsFtps(),
          getTargetProfileDistEmails(),
        ]);

        setReportTypes(rtResult.value || rtResult.Content || []);
        setScopes(scopesResult.value || scopesResult.Content || []);
        setNamingConventions(ncResult.value || ncResult.Content || []);
        setClientLanguages(clResult.value || clResult.Content || []);
        setAllReportLanguages(arlResult.value || arlResult.Content || []);
        setFtpTargets(ftpResult.value || ftpResult.Content || []);
        setEmailTargets(emailResult.value || emailResult.Content || []);

        if (isEditMode) {
          const [profileResult, reportsResult] = await Promise.all([
            getTargetProfileById(Number(id)),
            getReportsByProfileId(Number(id)),
          ]);

          const profile = profileResult.value?.[0] || profileResult.Content?.[0] || profileResult;
          setProfileName(profile.TargetProfileName || '');
          setIsActive(profile.IsActive ?? true);
          setIsRestricted(profile.IsRestricted ?? false);
          setSelectedScopeId(profile.ScopeId?.toString() || '');
          setArchiveSingleZip(profile.ArchiveSingleZip ?? false);
          setArchiveEncrypted(profile.ArchiveEncrypted ?? false);
          setDocumentPreference(profile.DocumentPreference || 'PerFund');
          setDeliveryMethod(profile.DeliveryMethod || 'None');
          setSelectedFtpId(profile.TargetFtpId?.toString() || '');
          setSelectedEmailId(profile.TargetEmailId?.toString() || '');

          const reports = reportsResult.value || reportsResult.Content || [];
          setExistingReports(reports);
          const selectedIds = new Set(reports.map((r) => r.ReportTypeId));
          setSelectedReportIds(selectedIds);

          // Build report configs from existing
          const configs = {};
          reports.forEach((r) => {
            configs[r.ReportTypeId] = {
              TargetReportId: r.TargetReportId,
              NamingConventionId: r.NamingConventionId || '',
              FileFormat: r.FileFormat || 'PDF',
              Languages: r.Languages || [],
            };
          });
          setReportConfigs(configs);
        }
      } catch (err) {
        context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to load profile data'}</> });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEditMode, context.handlers]);

  const toggleReport = useCallback((reportTypeId) => {
    setSelectedReportIds((prev) => {
      const next = new Set(prev);
      if (next.has(reportTypeId)) next.delete(reportTypeId);
      else next.add(reportTypeId);
      return next;
    });
  }, []);

  const openConfigModal = useCallback((modalType, reportTypeId) => {
    setModalReportId(reportTypeId);
    setActiveModal(modalType);
  }, []);

  const updateReportConfig = useCallback((reportTypeId, field, value) => {
    setReportConfigs((prev) => ({
      ...prev,
      [reportTypeId]: { ...(prev[reportTypeId] || {}), [field]: value },
    }));
  }, []);

  const handleSave = async () => {
    if (!profileName.trim()) {
      context.handlers.setToast({ type: 'error', body: <>Profile name is required</> });
      return;
    }

    try {
      setLoading(true);

      const profileData = {
        TargetProfileName: profileName,
        IsActive: isActive,
        IsRestricted: isRestricted,
        ScopeId: selectedScopeId ? Number(selectedScopeId) : null,
        ArchiveSingleZip: archiveSingleZip,
        ArchiveEncrypted: archiveEncrypted,
        ArchivePassword: archiveEncrypted ? archivePassword : null,
        DocumentPreference: documentPreference,
        DeliveryMethod: deliveryMethod,
        TargetFtpId: deliveryMethod === 'SFTP' && selectedFtpId ? Number(selectedFtpId) : null,
        TargetEmailId: deliveryMethod === 'Email' && selectedEmailId ? Number(selectedEmailId) : null,
      };

      let profileId;
      if (isEditMode) {
        profileData.TargetProfileId = Number(id);
        await updateTargetProfile(profileData);
        profileId = Number(id);
      } else {
        const result = await createTargetProfile(profileData);
        profileId = result.value?.[0]?.TargetProfileId || result.Content?.[0]?.TargetProfileId || result.TargetProfileId;
      }

      // Diff reports
      if (isEditMode) {
        // Delete removed reports
        const removedReports = existingReports.filter((r) => !selectedReportIds.has(r.ReportTypeId));
        for (const r of removedReports) {
          await deleteTargetReport(r.TargetReportId);
        }

        // Update existing reports
        const updatedReports = existingReports.filter((r) => selectedReportIds.has(r.ReportTypeId));
        for (const r of updatedReports) {
          const cfg = reportConfigs[r.ReportTypeId] || {};
          await updateTargetReport({
            TargetReportId: r.TargetReportId,
            TargetProfileId: profileId,
            ReportTypeId: r.ReportTypeId,
            NamingConventionId: cfg.NamingConventionId || r.NamingConventionId,
            FileFormat: cfg.FileFormat || r.FileFormat,
          });
        }

        // Create new reports
        const existingReportTypeIds = new Set(existingReports.map((r) => r.ReportTypeId));
        const newReportTypeIds = [...selectedReportIds].filter((rtId) => !existingReportTypeIds.has(rtId));
        for (const rtId of newReportTypeIds) {
          const cfg = reportConfigs[rtId] || {};
          await createTargetReport({
            TargetProfileId: profileId,
            ReportTypeId: rtId,
            NamingConventionId: cfg.NamingConventionId || null,
            FileFormat: cfg.FileFormat || 'PDF',
          });
        }
      } else {
        // Create all selected reports
        for (const rtId of selectedReportIds) {
          const cfg = reportConfigs[rtId] || {};
          await createTargetReport({
            TargetProfileId: profileId,
            ReportTypeId: rtId,
            NamingConventionId: cfg.NamingConventionId || null,
            FileFormat: cfg.FileFormat || 'PDF',
          });
        }
      }

      context.handlers.setToast({ type: 'success', body: <>Profile saved successfully</> });
      navigate('/distribution/target-profiles-list');
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to save profile'}</> });
    } finally {
      setLoading(false);
    }
  };

  // Reports grid column defs
  const reportColumnDefs = [
    {
      headerName: '',
      width: 50,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <input
          type="checkbox"
          className={classes.checkbox}
          checked={selectedReportIds.has(params.data.ReportTypeId)}
          onChange={() => toggleReport(params.data.ReportTypeId)}
        />
      ),
    },
    { headerName: 'Type', field: 'TypeName', flex: 1, filter: 'agTextColumnFilter' },
    {
      headerName: 'Naming',
      width: 100,
      sortable: false,
      filter: false,
      cellRenderer: (params) =>
        selectedReportIds.has(params.data.ReportTypeId) ? (
          <GrpButton
            size="Small"
            icon={['fas', 'pen']}
            iconSide="Round"
            onClick={() => openConfigModal('naming', params.data.ReportTypeId)}
          />
        ) : null,
    },
    {
      headerName: 'Language',
      width: 100,
      sortable: false,
      filter: false,
      cellRenderer: (params) =>
        selectedReportIds.has(params.data.ReportTypeId) ? (
          <GrpButton
            size="Small"
            icon={['fas', 'globe']}
            iconSide="Round"
            onClick={() => openConfigModal('language', params.data.ReportTypeId)}
          />
        ) : null,
    },
    {
      headerName: 'Format',
      width: 100,
      sortable: false,
      filter: false,
      cellRenderer: (params) =>
        selectedReportIds.has(params.data.ReportTypeId) ? (
          <GrpButton
            size="Small"
            icon={['fas', 'file']}
            iconSide="Round"
            onClick={() => openConfigModal('format', params.data.ReportTypeId)}
          />
        ) : null,
    },
  ];

  // Render config modals
  const renderModal = useCallback(() => {
    if (!activeModal) return null;
    
    if (activeModal === 'delivery') {
      console.log('Rendering delivery modal with:', { deliveryMethod, selectedFtpId, selectedEmailId, ftpTargets, emailTargets });  
      return (
        <div className={classes.modalOverlay} onClick={() => setActiveModal(null)}>
          <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHeader}>Delivery Method</div>
            <div className={classes.formGroup}>
              <label>Method</label>
              <select value={deliveryMethod} onChange={(e) => setDeliveryMethod(e.target.value)}>
                <option value="None">None</option>
                <option value="SFTP">SFTP</option>
                <option value="Email">Email</option>
              </select>
            </div>
            {deliveryMethod === 'SFTP' && (
              <div className={classes.formGroup}>
                <label>SFTP Site</label>
                <select value={selectedFtpId} onChange={(e) => setSelectedFtpId(e.target.value)}>
                  <option value="">-- Select --</option>
                  {ftpTargets.map((ftp) => (
                    <option key={ftp.TargetFtpId} value={ftp.TargetFtpId}>
                      {ftp.FtpSiteName || ftp.Host}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {deliveryMethod === 'Email' && (
              <div className={classes.formGroup}>
                <label>Email Target</label>
                <select value={selectedEmailId} onChange={(e) => setSelectedEmailId(e.target.value)}>
                  <option value="">-- Select --</option>
                  {emailTargets.map((em) => (
                    <option key={em.TargetEmailId} value={em.TargetEmailId}>
                      {em.EmailAddress || em.TargetEmailName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className={classes.modalActions}>
              <GrpButton size="Small" type="Primary" text="OK" onClick={() => setActiveModal(null)} />
              <GrpButton size="Small" type="Secondary" text="Cancel" onClick={() => setActiveModal(null)} />
            </div>
          </div>
        </div>
      );
    }
    
    if (!activeModal || !modalReportId) return null;
    const cfg = reportConfigs[modalReportId] || {};

    if (activeModal === 'naming') {
      return (
        <div className={classes.modalOverlay} onClick={() => setActiveModal(null)}>
          <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHeader}>Naming Convention</div>
            <div className={classes.formGroup}>
              <label>Convention</label>
              <select
                value={cfg.NamingConventionId || ''}
                onChange={(e) => updateReportConfig(modalReportId, 'NamingConventionId', e.target.value)}
              >
                <option value="">-- Select --</option>
                {namingConventions.map((nc) => (
                  <option key={nc.NamingConventionId} value={nc.NamingConventionId}>
                    {nc.NamingConventionName || nc.Convention}
                  </option>
                ))}
              </select>
            </div>
            <div className={classes.modalActions}>
              <GrpButton size="Small" type="Primary" text="OK" onClick={() => setActiveModal(null)} />
            </div>
          </div>
        </div>
      );
    }

    if (activeModal === 'language') {
      const selectedLangs = cfg.Languages || [];
      return (
        <div className={classes.modalOverlay} onClick={() => setActiveModal(null)}>
          <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHeader}>Languages</div>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {clientLanguages.map((lang) => (
                <label key={lang.IsoLanguage} className={classes.checkboxLabel} style={{ display: 'block', marginBottom: 4 }}>
                  <input
                    type="checkbox"
                    className={classes.checkbox}
                    checked={selectedLangs.includes(lang.IsoLanguage)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...selectedLangs, lang.IsoLanguage]
                        : selectedLangs.filter((l) => l !== lang.IsoLanguage);
                      updateReportConfig(modalReportId, 'Languages', next);
                    }}
                  />
                  {lang.LanguageName || lang.IsoLanguage}
                </label>
              ))}
            </div>
            <div className={classes.modalActions}>
              <GrpButton size="Small" type="Primary" text="OK" onClick={() => setActiveModal(null)} />
            </div>
          </div>
        </div>
      );
    }

    if (activeModal === 'format') {
      return (
        <div className={classes.modalOverlay} onClick={() => setActiveModal(null)}>
          <div className={classes.modal} onClick={(e) => e.stopPropagation()}>
            <div className={classes.modalHeader}>File Format</div>
            <div className={classes.formGroup}>
              <label>Format</label>
              <select
                value={cfg.FileFormat || 'PDF'}
                onChange={(e) => updateReportConfig(modalReportId, 'FileFormat', e.target.value)}
              >
                <option value="PDF">PDF</option>
                <option value="WORD">Word</option>
                <option value="EXCEL">Excel</option>
                <option value="XML">XML</option>
              </select>
            </div>
            <div className={classes.modalActions}>
              <GrpButton size="Small" type="Primary" text="OK" onClick={() => setActiveModal(null)} />
            </div>
          </div>
        </div>
      );
    }
    console.log('Rendering modal for:', { activeModal, modalReportId, reportConfigs });

    return null;
  }, [activeModal, modalReportId, reportConfigs, namingConventions, clientLanguages, deliveryMethod, ftpTargets, emailTargets]);

  if (loading) return <GrpSpinner />;
  return (
    <div className={classes.container}>
      {renderModal()}

      {/* Header */}
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <input
            className={classes.profileNameInput}
            placeholder="Profile Name"
            value={profileName}
            onChange={(e) => setProfileName(e.target.value)}
          />
          <label className={classes.checkboxLabel}>
            <input type="checkbox" className={classes.checkbox} checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            Active
          </label>
          <label className={classes.checkboxLabel}>
            <input type="checkbox" className={classes.checkbox} checked={isRestricted} onChange={(e) => setIsRestricted(e.target.checked)} />
            Restricted
          </label>
        </div>
        <div className={classes.headerActions}>
          <GrpButton size="Small" type="Primary" text="Save" onClick={handleSave} />
          <GrpButton size="Small" type="Secondary" text="Cancel" onClick={() => navigate('/distribution/target-profiles-list')} />
        </div>
      </div>

      {/* Reports + Options */}
      <div className={classes.sectionsRow}>
        {/* Reports grid */}
        <div className={`${classes.section} ${classes.reportsGrid}`}>
          <div className={classes.sectionHeader}>Reports</div>
          <div className={`ag-theme-balham ${classes.gridContainer}`}>
            <AgGridReact
              ref={reportsGridRef}
              rowData={reportTypes}
              columnDefs={reportColumnDefs}
              domLayout="normal"
              defaultColDef={{ sortable: true, resizable: true }}
              getRowId={(params) => String(params.data.ReportTypeId)}
              suppressRowClickSelection
            />
          </div>
        </div>

        {/* Options */}
        <div className={`${classes.section} ${classes.optionsSection}`}>
          <div className={classes.sectionHeader}>Options</div>
          <div className={classes.sectionBody}>

            <div className={classes.formGroup}>
              <label>Archive Settings</label>
              <label className={classes.checkboxLabel}>
                <input
                  type="checkbox"
                  className={classes.checkbox}
                  checked={archiveSingleZip}
                  onChange={(e) => setArchiveSingleZip(e.target.checked)}
                />
                Archive all documents into one zip file?
              </label>
            </div>


            {archiveSingleZip && (
              <div className={classes.formGroup} style={{ marginTop: 8 }}>
                <label className={classes.checkboxLabel}>
                  <input
                    type="radio"
                    className={classes.checkbox}
                    name="archiveEncrypted"
                    checked={!archiveEncrypted}
                    onChange={() => setArchiveEncrypted(false)}
                  />
                  Unencrypted Archive
                </label>
                <label className={classes.checkboxLabel}>
                  <input
                    type="radio"
                    className={classes.checkbox}
                    name="archiveEncrypted"
                    checked={archiveEncrypted}
                    onChange={() => setArchiveEncrypted(true)}
                  />
                  Encrypted Archive
                </label>
              </div>
            )}

            {archiveSingleZip && archiveEncrypted && (
              <div className={classes.formGroup} style={{ marginTop: 8 }}>
                <label>Archive Password</label>
                <input
                  type="password"
                  value={archivePassword}
                  onChange={(e) => setArchivePassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
            )}
            
            <div className={classes.formGroup}>
              <label>Document Preference</label>
              <div>
                <label className={classes.checkboxLabel}>
                  <input
                    type="radio"
                    className={classes.checkbox}
                    name="documentPreference"
                    value="PerFund"
                    checked={documentPreference === 'PerFund'}
                    onChange={(e) => setDocumentPreference(e.target.value)}
                  />
                  One file per fund
                </label>
                <label className={classes.checkboxLabel}>
                  <input
                    type="radio"
                    className={classes.checkbox}
                    name="documentPreference"
                    value="All"
                    checked={documentPreference === 'All'}
                    onChange={(e) => setDocumentPreference(e.target.value)}
                  />
                  All in one file
                </label>
              </div>
            </div>

            <div className={classes.formGroup}>
              <label>Scope</label>
              <select value={selectedScopeId} onChange={(e) => setSelectedScopeId(e.target.value)}>
                <option value="">-- None --</option>
                {scopes.map((s) => (
                  <option key={s.ScopeId} value={s.ScopeId}>{s.ScopeName}</option>
                ))}
              </select>
            </div>
          
          </div>
        </div>
      </div>

      {/* Delivery Method */}
      <div className={classes.deliverySection}>
        <div className={classes.deliveryHeader}>Delivery Method</div>
        <div className={classes.deliveryActions}>
          <span>Method: <strong>{deliveryMethod}</strong></span>
          <GrpButton size="Small" type="Secondary" text="Configure" onClick={() => setActiveModal('delivery')} />
        </div>
        {deliveryMethod === 'SFTP' && selectedFtpId && (
          <div className={classes.deliverySummary}>
            SFTP: {ftpTargets.find((f) => String(f.TargetFtpId) === selectedFtpId)?.FtpSiteName || selectedFtpId}
          </div>
        )}
        {deliveryMethod === 'Email' && selectedEmailId && (
          <div className={classes.deliverySummary}>
            Email: {emailTargets.find((e) => String(e.TargetEmailId) === selectedEmailId)?.EmailAddress || selectedEmailId}
          </div>
        )}
      </div>
    </div>
  );
};

export default TargetProfilePage;
